import React, { useContext } from "react";
import styled from "styled-components";
import { AudienceType, IAnnouncement, IIcon } from "../announcements.model";
import { useTranslation } from "react-i18next";
import { CancelIcon } from "@wit/mpesa-ui-components";
import { AnnouncementsUtils } from "../announcements.utils";
import { ConfigContext } from "../../../app.component";

interface IHomeScreenDetailInterface {
  valuesAnnouncement: IAnnouncement;
  icon: IIcon;
  backgroundImage?: string;
}

/**
 * component to preview the card of announcement in home screen
 * @param param0 interface with data necessary
 */
const HomeScreenAnnouncementBusiness = ({ valuesAnnouncement, icon, backgroundImage }: IHomeScreenDetailInterface) => {
  const [t] = useTranslation();
  const colorDefault = "#2fc56d";
  const { config } = useContext(ConfigContext);
  const isNewBusinessCard = AnnouncementsUtils.validateIsNewBusinessCard(config, valuesAnnouncement.targetVersions);

  return (
    <DivBackground
      initial={!valuesAnnouncement.title && !valuesAnnouncement.smallDescription && icon.id === "000"}
      audience={valuesAnnouncement.audience}
      isNewBusinessAnnouncements={isNewBusinessCard}
    >
      {isNewBusinessCard ? (
        <AnnouncementETDiv
          backgroundImage={backgroundImage || ""}
          showShadow={!!valuesAnnouncement.title || !!valuesAnnouncement.smallDescription}
          id={`content-${config!.client}`}
        >
          <DetailETDiv>
            <TitleETDiv>
              <span>{valuesAnnouncement.title ? valuesAnnouncement.title : ""}</span>
            </TitleETDiv>
            <DescriptionETDiv>
              <span>{valuesAnnouncement.smallDescription ? valuesAnnouncement.smallDescription : ""}</span>
            </DescriptionETDiv>
          </DetailETDiv>
        </AnnouncementETDiv>
      ) : (
        <AnnouncementDiv audience={valuesAnnouncement.audience}>
          <IconDiv color={valuesAnnouncement.color ? valuesAnnouncement.color : colorDefault}>
            <IconDropDownContainer
              color={valuesAnnouncement.color ? valuesAnnouncement.color : colorDefault}
              logo={icon.base64}
            />
          </IconDiv>
          <DetailDiv color={valuesAnnouncement.color ? valuesAnnouncement.color : colorDefault}>
            <TitleDiv>
              <span>
                {valuesAnnouncement.title ? valuesAnnouncement.title : t("pages.announcements.addPage.defaultTitle")}
              </span>
            </TitleDiv>
            <DescriptionDiv>
              <span>
                {valuesAnnouncement.smallDescription
                  ? valuesAnnouncement.smallDescription
                  : t("pages.announcements.addPage.defaultDescription")}
              </span>
            </DescriptionDiv>
            {valuesAnnouncement.dismissible ? (
              <CancelIconContainer>
                <CancelIcon width={"9px"} height={"9px"} style={{ stroke: "white" }} />
              </CancelIconContainer>
            ) : null}
          </DetailDiv>
        </AnnouncementDiv>
      )}
    </DivBackground>
  );
};

export default HomeScreenAnnouncementBusiness;

const DivBackground = styled("div")<{
  initial: boolean;
  audience: AudienceType | undefined;
  isNewBusinessAnnouncements?: boolean;
}>`
  background: ${props =>
    props.audience && AnnouncementsUtils.isConsumerAudience(props.audience)
      ? "url('./images/screensAppSFC/screenConsumerDeprecated.png')"
      : props.isNewBusinessAnnouncements
      ? "url('./images/screensAppSFC/screenBusiness_et.png')"
      : "url('./images/screensAppSFC/screenBusiness.png')"};
  background-size: cover;
  background-color: transparent !important;
  width: 100%;
  height: 100%;
  ${props =>
    props.initial
      ? "webkit-filter: opacity(0.5) drop-shadow(0 0 0 white);filter: opacity(0.5) drop-shadow(0 0 0 white);"
      : null}
`;

const AnnouncementETDiv = styled("div")<{
  backgroundImage: string;
  showShadow: boolean;
}>`
  position: absolute;
  width: 45%;
  margin: auto;
  height: 68px;
  top: 42%;
  left: 10px;
  display: flex;
  flex-direction: column;
  border-radius: 6px;
  background: ${props => {
    let backgroundComposition = props.showShadow
      ? `linear-gradient(360deg, rgba(0, 0, 0, 0.6) 0%, rgba(0 0 0/13%) 100%),`
      : `linear-gradient(360deg, rgba(0, 0, 0, 0.1) 0%, rgba(0 0 0/13%) 50%),`;
    backgroundComposition += props.backgroundImage
      ? `url("${props.backgroundImage}")`
      : "url('./images/gallery_filled.png')";
    return backgroundComposition;
  }};
  background-size: ${props => {
    if (!props.backgroundImage) {
      return "";
    }
    return "100% 80px";
  }};
  background-repeat: no-repeat;
  background-position: center;
`;

const AnnouncementDiv = styled("div")<{
  audience: AudienceType | undefined;
}>`
  position: absolute;
  width: 88%;
  margin: auto;
  height: 45px;
  top: ${props => (props.audience && AnnouncementsUtils.isConsumerAudience(props.audience) ? "39.75%" : "36.75%")};
  left: 6.5%;
  display: flex;
  flex-direction: row;
`;

const IconDiv = styled("div")<{ color: string }>`
  width: 20%;
  height: 100%;
  background-color: ${props => props.color}32;
  border-top-left-radius: 5.6px;
  border-bottom-left-radius: 5.6px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;
const DetailETDiv = styled("div")<{}>`
  width: 100%;
  padding: 0 5px 0 10px;
  border-bottom-right-radius: 5.6px;
  border-top-right-radius: 5.6px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  font-family: Vodafone Rg;
  position: relative;
  flex: 2;
`;
const DetailDiv = styled("div")<{ color: string }>`
  width: 80%;
  height: 100%;
  background-color: ${props => props.color}16;
  border-bottom-right-radius: 5.6px;
  border-top-right-radius: 5.6px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  font-family: Vodafone Rg;
  position: relative;
`;

const TitleETDiv = styled("div")<{}>`
  font-family: Vodafone Rg;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;
  font-size: 12px;
  color: #fff;
  text-transform: uppercase;
  line-height: 0.9;
`;

const TitleDiv = styled("div")`
  margin-left: 5px;
  font-size: 9.8px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #666666;
  word-break: break-word;
`;

const DescriptionETDiv = styled("div")<{}>`
  font-style: normal;
  font-size: 10px;
  font-weight: normal;
  color: #fff;
  font-family: Vodafone Rg;
  width: 100%;
  line-height: 0.9;
  word-break: break-word;
`;

const DescriptionDiv = styled("div")`
  margin-left: 5px;
  font-size: 8.4px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  font-weight: normal;
  color: #aaaaaa;
`;

const IconDropDownContainer = styled("div")<{
  logo: string;
  color: string;
}>`
  background-image: ${props => (props.logo ? `url("${props.logo}")` : "none")};
  -webkit-filter: opacity(0.5) drop-shadow(0 0 0 ${props => props.color});
  filter: opacity(0.5) drop-shadow(0 0 0 ${props => props.color});
  background-repeat: no-repeat;
  background-size: 22px 22px;
  background-position: center;
  width: 22px;
  height: 22px;
`;

const CancelIconContainer = styled("div")`
  position: absolute;
  width: 15px;
  height: 15px;
  right: 8px;
  top: 8px;
  border-radius: 50%;
  background: rgb(160, 160, 160);
  display: flex;
  justify-content: center;
  align-items: center;
`;
